import { supabase } from '../config/supabase';

// Promotional Baseline Configuration
const BASELINE_COUNT = 1349;
const LAUNCH_DATE = new Date('2025-01-01T00:00:00Z');
const GROWTH_RATE_PER_HOUR = 0.45;

const getLocalIncrement = () => {
  return parseInt(localStorage.getItem('clipshares_local_rooms') || '0');
};

const incrementLocalCount = () => {
  const current = getLocalIncrement();
  localStorage.setItem('clipshares_local_rooms', (current + 1).toString());
};

const generateRoomName = () => {
  const adjectives = ['Swift', 'Bright', 'Cosmic', 'Digital', 'Electric', 'Future', 'Golden', 'Hidden', 'Infinite', 'Mystic'];
  const nouns = ['Cloud', 'Bridge', 'Portal', 'Space', 'Zone', 'Hub', 'Lab', 'Studio', 'Vault', 'Chamber'];
  return `${adjectives[Math.floor(Math.random() * adjectives.length)]}${nouns[Math.floor(Math.random() * nouns.length)]}${Math.floor(Math.random() * 9999)}`;
};

const generatePassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let password = '';
  for (let i = 0; i < 10; i++) password += chars[Math.floor(Math.random() * chars.length)];
  return password;
};

export const getLocationFromIp = async (ip) => {
  if (!ip || ip === '127.0.0.1' || ip.startsWith('192.168')) return 'Somewhere';
  try {
    const res = await fetch(`https://ipapi.co/${ip}/json/`);
    const data = await res.json();
    if (data.city && data.region) return `${data.city}, ${data.region}`;
    return data.country_name || 'Somewhere';
  } catch (e) {
    return 'Somewhere';
  }
};

const getClientIp = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    return null;
  }
};

export const roomService = {
  async createRoom() {
    try {
      const ipAddress = await getClientIp();
      const location = await getLocationFromIp(ipAddress);
      const roomName = generateRoomName();
      const password = generatePassword();
      const { data, error } = await supabase
        .from('rooms')
        .insert([{
          room_name: roomName,
          password: password,
          creator_ip: ipAddress,
          created_at: new Date().toISOString(),
          last_activity: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      incrementLocalCount();
      return { success: true, location, room: { name: data.room_name, password: data.password, createdAt: data.created_at } };
    } catch (error) {
      console.error('[RoomService] Create error:', error);
      return { success: false, error: error.message };
    }
  },

  async verifyRoom(roomName, password) {
    try {
      if (!roomName || !password) return { success: false, error: 'Missing credentials' };
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .eq('room_name', roomName)
        .eq('password', password)
        .maybeSingle();

      if (error) throw error;
      if (!data) return { success: false, error: 'Room not found or invalid password' };
      return { success: true, room: data };
    } catch (error) {
      console.error('[RoomService] Verify error:', error);
      return { success: false, error: 'Failed to verify room' };
    }
  },

  async uploadFile(roomName, file) {
    try {
      const sanitizedName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      const fileName = `${roomName}/${Date.now()}-${sanitizedName}`;
      const { data, error } = await supabase.storage
        .from('clipshares_files')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage
        .from('clipshares_files')
        .getPublicUrl(fileName);
      
      return { success: true, url: publicUrl, name: file.name, size: file.size, type: file.type };
    } catch (error) {
      console.error('[RoomService] Upload error:', error);
      return { success: false, error: error.message };
    }
  },

  async getTotalRoomCount() {
    try {
      const { data } = await supabase.from('site_statistics').select('total_rooms_created').eq('id', 'global').maybeSingle();
      const dbCount = data?.total_rooms_created || BASELINE_COUNT;
      const hoursSinceLaunch = (new Date() - LAUNCH_DATE) / (1000 * 60 * 60);
      const timeBasedGrowth = Math.floor(hoursSinceLaunch * GROWTH_RATE_PER_HOUR);
      return { success: true, count: dbCount + timeBasedGrowth + getLocalIncrement() };
    } catch (error) {
      return { success: true, count: BASELINE_COUNT + getLocalIncrement() };
    }
  },

  async getActiveRoomCount() {
    try {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count } = await supabase.from('rooms').select('*', { count: 'exact', head: true }).gt('created_at', oneHourAgo);
      return { success: true, count: count || 0 };
    } catch (error) {
      return { success: false, count: 0 };
    }
  },

  async expireRoomNow(roomName) {
    try {
      // First try calling the RPC if it exists
      const { error: rpcError } = await supabase.rpc('expire_room_now', { room_name_param: roomName });
      
      if (rpcError) {
        // Fallback to direct delete via RLS
        const { error: deleteError } = await supabase
          .from('rooms')
          .delete()
          .eq('room_name', roomName);
          
        if (deleteError) throw deleteError;
      }
      return { success: true };
    } catch (error) {
      console.error('[RoomService] Destruction error:', error);
      return { success: false, error: error.message };
    }
  },

  async updateRoomActivity(roomName) {
    try {
      await supabase.from('rooms').update({ last_activity: new Date().toISOString() }).eq('room_name', roomName);
    } catch (e) { /* silent fail */ }
  },

  async getRoomExpiration(roomName) {
    try {
      const { data } = await supabase.from('rooms').select('created_at').eq('room_name', roomName).maybeSingle();
      if (!data) return { success: false, isExpired: true };
      const minutesSince = (new Date() - new Date(data.created_at)) / (1000 * 60);
      return { success: true, timeRemaining: Math.max(0, 60 - Math.floor(minutesSince)), isExpired: minutesSince >= 60 };
    } catch (e) {
      return { success: false, isExpired: false };
    }
  },

  async checkRoomExists(roomName) {
    try {
      const { data } = await supabase.from('rooms').select('room_name').eq('room_name', roomName).maybeSingle();
      return { exists: !!data };
    } catch (e) {
      return { exists: true };
    }
  },

  async getCleanupStats() {
    try {
      const { data, error } = await supabase.rpc('expire_and_delete_rooms');
      if (error) throw error;
      return { success: true, stats: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};